'use client'

import { createTranslator } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { recoverFromBoundaryError } from '@/lib/errors/error-boundary-recovery'
import {
  DEFAULT_LOCALE,
  type Locale,
  localeFromLanguageTag,
  toLanguageTag,
} from '@/lib/i18n/config'
import englishErrorPage from '@/messages/en/errorPage.json'

type ErrorPageMessages = typeof englishErrorPage

function reloadPage() {
  window.location.reload()
}

/**
 * The language of the page that failed: on the client the failed document
 * is still in place during this boundary's first render; on the server
 * there is none, and the page reads English.
 */
function readFailedPageLocale(): Locale {
  if (typeof document === 'undefined') {
    return DEFAULT_LOCALE
  }
  return localeFromLanguageTag(document.documentElement.lang) ?? DEFAULT_LOCALE
}

/**
 * The copy in the failed page's language. No catalog provider survives a
 * root-layout failure, so its catalog loads on its own (English until it
 * arrives).
 */
function useGlobalErrorCopy() {
  const [locale] = useState(readFailedPageLocale)
  const [messages, setMessages] = useState<ErrorPageMessages | null>(null)
  useEffect(() => {
    if (locale === DEFAULT_LOCALE) {
      return
    }
    let isCurrent = true
    import('@/components/i18n/standalone-catalogs')
      .then((catalogs) => catalogs.loadErrorPageCatalog(locale))
      .then((catalog) => {
        if (isCurrent) {
          setMessages(catalog)
        }
      })
      .catch(() => {
        // English stays: a missing chunk must not break the error page.
      })
    return () => {
      isCurrent = false
    }
  }, [locale])
  const shown = messages === null ? DEFAULT_LOCALE : locale
  const t = useMemo(
    () =>
      createTranslator({
        locale: shown,
        messages: { errorPage: messages ?? englishErrorPage },
        namespace: 'errorPage',
      }),
    [messages, shown]
  )
  return { t, lang: toLanguageTag(shown) }
}

/**
 * Root error boundary. Replaces the entire root layout when an
 * unrecoverable error escapes the app, so it must render its own
 * `<html><body>` and cannot rely on global Tailwind layers being
 * present. Inline styles only.
 *
 * The thrown error is forwarded into Datadog RUM (when initialized). A
 * stale-deploy `ChunkLoadError` additionally spends the session's single
 * automatic reload, shared with the route boundary. Its copy follows the
 * failed page's language (`useGlobalErrorCopy`).
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
  // `reset` is part of Next.js's contract but intentionally unused here:
  // the global boundary fires after the root layout itself has thrown,
  // so re-rendering rarely recovers; a full reload from the user is
  // usually the right next step.
  reset: () => void
}) {
  const { t, lang } = useGlobalErrorCopy()
  useEffect(() => {
    recoverFromBoundaryError(error)
  }, [error])

  return (
    <html lang={lang}>
      <body
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100svh',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{t('title')}</h2>
        <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>{t('description')}</p>
        <button
          type="button"
          onClick={reloadPage}
          style={{
            border: 0,
            borderRadius: '0.75rem',
            padding: '0.625rem 1rem',
            background: '#111827',
            color: '#ffffff',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 600,
          }}
        >
          {t('reload')}
        </button>
      </body>
    </html>
  )
}
